import { Fragment, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import { toUtcEndOfDay, toUtcMidnight } from '../../utils/dateFormat';

import { STATUS_COLORS } from './colors';
import ValueBadge from './ValueBadge';

const COLUMNS = [
  { key: 'runId', label: 'Run ID' },
  { key: 'datetime', label: 'Execution Time' },
  { key: 'promptText', label: 'Prompt' },
  { key: 'promptType', label: 'Prompt Type' },
  { key: 'testStatus', label: 'Test Result' },
  { key: 'categorizationCheckScore', label: 'Quality Score' },
];

const TOTAL_COLUMN_COUNT = COLUMNS.length + 1; // + expand toggle column

const PAGE_SIZE = 10;

function StatusPill({ status }) {
  const color = STATUS_COLORS[(status || '').toLowerCase()] || '#6b7280';
  return <span className="sense-shield-pill" style={{ background: color }}>{status}</span>;
}

function DatadogTraceLink({ link }) {
  if (!link) {
    return <span className="sense-shield-reasoning-empty">—</span>;
  }

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="sense-shield-copy-btn"
      onClick={(event) => event.stopPropagation()}
    >
      Open in Datadog
    </a>
  );
}

function formatFieldName(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function ReasoningValue({ value }) {
  if (value === null || value === undefined || value === '') {
    return <span className="sense-shield-reasoning-empty">—</span>;
  }
  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="sense-shield-reasoning-empty">—</span>;
    }
    return (
      <ul className="sense-shield-reasoning-list">
        {value.map((item, idx) => (
          <li key={idx}><ReasoningValue value={item} /></li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    return (
      <ul className="sense-shield-reasoning-list">
        {Object.entries(value).map(([key, val]) => (
          <li key={key}>
            <span className="sense-shield-reasoning-key">{formatFieldName(key)}:</span>{' '}
            {typeof val === 'object' && val !== null ? <ReasoningValue value={val} /> : <span>{String(val)}</span>}
          </li>
        ))}
      </ul>
    );
  }
  return <span>{String(value)}</span>;
}

function ReasoningDetails({ raw }) {
  if (!raw) {
    return <span className="sense-shield-reasoning-empty">No reasoning data</span>;
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return <p className="sense-shield-reasoning-raw">{raw}</p>;
  }
  return <ReasoningValue value={parsed} />;
}

function ExecutionDetailPanel({ row }) {
  return (
    <div className="sense-shield-detail-panel">
      <div className="sense-shield-detail-grid">
        <div className="sense-shield-detail-item">
          <span className="sense-shield-detail-label">Business Impact</span>
          <span className="sense-shield-detail-value">{row.businessImpactScore ?? '—'}</span>
        </div>
        <div className="sense-shield-detail-item">
          <span className="sense-shield-detail-label">Failure Category</span>
          <span className="sense-shield-detail-value">{row.failureCategory ?? '—'}</span>
        </div>
        <div className="sense-shield-detail-item">
          <span className="sense-shield-detail-label">Bugs Found</span>
          <span className="sense-shield-detail-value">{row.bugsFound ?? '—'}</span>
        </div>
        <div className="sense-shield-detail-item">
          <span className="sense-shield-detail-label">Datadog Trace</span>
          <span className="sense-shield-detail-value">
            <DatadogTraceLink link={row.datadogLink} />
          </span>
        </div>
      </div>
      <div className="sense-shield-detail-item sense-shield-detail-item-full">
        <span className="sense-shield-detail-label">Prompt</span>
        <span className="sense-shield-detail-value sense-shield-detail-prompt">{row.promptText ?? '—'}</span>
      </div>
      <div className="sense-shield-detail-reasoning">
        <span className="sense-shield-detail-label">Reasoning</span>
        <ReasoningDetails raw={row.categorizationCheckDetails} />
      </div>
    </div>
  );
}

function rowKeyFor(row, idx) {
  return `${row.runId}-${row.datetime}-${idx}`;
}

function LatestExecutionsTable({
  dateRange,
  promptTypes,
  testStatuses = [],
  failureCategories = [],
  promptId = null,
  deepLinkRunId = null,
  deepLinkPromptId = null,
}) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();

    if (deepLinkRunId) {
      // A deep link resolves one exact run (+ prompt) regardless of the current filters/date
      // range — the backend ignores the date range whenever runId is set.
      params.set('runId', deepLinkRunId);
      if (deepLinkPromptId) params.set('promptId', deepLinkPromptId);
    } else {
      params.set('from', toUtcMidnight(dateRange.startDate).toISOString());
      params.set('to', toUtcEndOfDay(dateRange.endDate).toISOString());
      promptTypes.forEach((promptType) => params.append('promptType', promptType));
      testStatuses.forEach((testStatus) => params.append('testStatus', testStatus));
      failureCategories.forEach((failureCategory) => params.append('failureCategory', failureCategory));
      if (promptId) params.set('promptId', promptId);
    }
    params.set('page', page.toString());
    params.set('pageSize', PAGE_SIZE.toString());

    api.get(`${QUALITY_SERVICE_API_URL}/hub/sense-shield/latest-executions?${params.toString()}`)
      .then((res) => {
        setData(res.data);
        if (deepLinkRunId && res.data?.data?.length) {
          const matchIdx = deepLinkPromptId
            ? res.data.data.findIndex((row) => row.promptId === deepLinkPromptId)
            : 0;
          const row = res.data.data[matchIdx >= 0 ? matchIdx : 0];
          setExpandedRowKey(rowKeyFor(row, matchIdx >= 0 ? matchIdx : 0));
        } else {
          setExpandedRowKey(null);
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    if (!deepLinkRunId) setExpandedRowKey(null);
  }, [dateRange, promptTypes, testStatuses, failureCategories, promptId, page, deepLinkRunId, deepLinkPromptId]);

  function toggleRow(rowKey) {
    setExpandedRowKey((current) => (current === rowKey ? null : rowKey));
  }

  if (loading || !data) {
    return (
      <div>
        <div className="sense-shield-panel-title">Latest Executions</div>
        <div className="sense-shield-panel-content">
          <div className="sense-shield-empty">{loading ? 'Loading...' : 'No data available'}</div>
        </div>
      </div>
    );
  }

  const { data: rows, page: currentPage, total, totalPages } = data;

  return (
    <div>
      <div className="sense-shield-panel-title">Latest Executions</div>
      <div className="sense-shield-panel-content sense-shield-table-wrapper">
        <table className="sense-shield-table">
          <thead>
            <tr>
              <th className="sense-shield-expand-col" />
              {COLUMNS.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const rowKey = rowKeyFor(row, idx);
              const isExpanded = expandedRowKey === rowKey;
              return (
                <Fragment key={rowKey}>
                  <tr
                    className="sense-shield-row-clickable"
                    onClick={() => toggleRow(rowKey)}
                  >
                    <td className="sense-shield-expand-col">
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </td>
                    <td>{row.runId ?? '—'}</td>
                    <td>{new Date(row.datetime).toLocaleString()}</td>
                    <td className="sense-shield-cell-wrap" title={row.promptText}>{row.promptText}</td>
                    <td><ValueBadge value={row.promptType} /></td>
                    <td><StatusPill status={row.testStatus} /></td>
                    <td>{row.categorizationCheckScore != null ? Math.round(row.categorizationCheckScore) : '—'}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="sense-shield-detail-row">
                      <td colSpan={TOTAL_COLUMN_COUNT}>
                        <ExecutionDetailPanel row={row} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={TOTAL_COLUMN_COUNT} className="sense-shield-empty">No data for the selected filters</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {total > 0 && (
        <div className="sense-shield-pagination">
          <span className="sense-shield-pagination-range">
            {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, total)} of {total}
          </span>
          <div className="sense-shield-pagination-controls">
            <button
              type="button"
              className="sense-shield-pagination-nav-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Prev
            </button>
            <span className="sense-shield-pagination-page-badge">{currentPage}</span>
            <button
              type="button"
              className="sense-shield-pagination-nav-btn"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LatestExecutionsTable;
